import { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, User, Search, Package, Briefcase, MapPin } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '@/App';

function Marketplace() {
  const { user, updateBalance, API } = useContext(AppContext);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [maxDistance, setMaxDistance] = useState('all');
  const [sortByDistance, setSortByDistance] = useState(false);

  const categories = ['All', 'Education', 'Technology', 'Creative', 'Health', 'Home', 'Other'];
  const distanceOptions = [
    { value: 'all', label: 'Anywhere' },
    { value: '5', label: 'Within 5 km' },
    { value: '10', label: 'Within 10 km' },
    { value: '20', label: 'Within 20 km' },
    { value: '50', label: 'Within 50 km' }
  ];

  useEffect(() => {
    fetchServices();
  }, [selectedType, selectedCategory, maxDistance, sortByDistance]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedType !== 'all') params.type = selectedType;
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (maxDistance !== 'all') params.max_distance = parseFloat(maxDistance);
      if (sortByDistance) params.sort_by_distance = true;
      
      const response = await axios.get(`${API}/services`, { params });
      // Filter out user's own services
      const filteredServices = response.data.filter(s => s.username !== user?.username);
      setServices(filteredServices);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchServices();
      return;
    }
    
    setLoading(true);
    try {
      const params = { search: searchQuery };
      if (selectedType !== 'all') params.type = selectedType;
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (maxDistance !== 'all') params.max_distance = parseFloat(maxDistance);
      if (sortByDistance) params.sort_by_distance = true;
      
      const response = await axios.get(`${API}/services`, { params });
      const filteredServices = response.data.filter(s => s.username !== user?.username);
      setServices(filteredServices);
    } catch (error) {
      console.error('Error searching services:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestService = async (service) => {
    if (user?.time_tokens < service.time_cost) {
      toast.error('Insufficient time tokens');
      return;
    }

    setRequesting(service.id);
    try {
      await axios.post(`${API}/transactions/request`, { service_id: service.id });
      toast.success(`Request sent! Waiting for ${service.username} to accept.`);
      
      // No balance update - happens after verification
      // Refresh services
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request service');
    } finally {
      setRequesting(null);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Education': 'bg-blue-100 text-blue-800',
      'Technology': 'bg-purple-100 text-purple-800',
      'Creative': 'bg-pink-100 text-pink-800',
      'Health': 'bg-green-100 text-green-800',
      'Home': 'bg-orange-100 text-orange-800',
      'Other': 'bg-gray-100 text-gray-800',
    };
    return colors[category] || colors['Other'];
  };

  const filteredByType = selectedType === 'all' 
    ? services 
    : services.filter(s => s.type === selectedType);

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="marketplace">
      <div>
        <h1 className="text-4xl font-bold text-gray-800 mb-2">Marketplace</h1>
        <p className="text-gray-600">Browse and request services and goods from the community</p>
      </div>

      {/* Search and Filters */}
      <Card className="bg-white shadow-lg">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search services or goods..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="search-input"
                  className="flex-1"
                />
                <Button onClick={handleSearch} data-testid="search-button">
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>
            </div>
            <div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.filter(c => c !== 'All').map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={maxDistance} onValueChange={setMaxDistance}>
                <SelectTrigger data-testid="distance-filter">
                  <SelectValue placeholder="Distance" />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  {distanceOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Sort by distance toggle */}
          {user?.location_enabled && (
            <div className="mt-4 flex items-center gap-2">
              <input
                type="checkbox"
                id="sort-distance"
                checked={sortByDistance}
                onChange={(e) => setSortByDistance(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="sort-distance" className="text-sm text-gray-700 cursor-pointer">
                Sort by distance (nearest first)
              </label>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Type Tabs */}
      <Tabs value={selectedType} onValueChange={setSelectedType} className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-white">
          <TabsTrigger value="all" data-testid="tab-all">
            All ({services.length})
          </TabsTrigger>
          <TabsTrigger value="service" data-testid="tab-services">
            <Briefcase className="w-4 h-4 mr-2" />
            Services ({services.filter(s => s.type === 'service').length})
          </TabsTrigger>
          <TabsTrigger value="good" data-testid="tab-goods">
            <Package className="w-4 h-4 mr-2" />
            Goods ({services.filter(s => s.type === 'good').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={selectedType} className="mt-6">
          {filteredByType.length === 0 ? (
            <Card className="bg-white shadow-lg">
              <CardContent className="py-12">
                <p className="text-center text-gray-500">
                  No {selectedType === 'all' ? 'items' : selectedType === 'service' ? 'services' : 'goods'} available at the moment
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredByType.map((service) => (
                <Card key={service.id} className="bg-white shadow-lg service-card" data-testid={`service-${service.id}`}>
                  {service.images && service.images.length > 0 && (
                    <div className="relative">
                      <img 
                        src={service.images[0]} 
                        alt={service.title}
                        className="w-full h-48 object-cover rounded-t-lg"
                      />
                      {service.images.length > 1 && (
                        <Badge className="absolute top-2 right-2 bg-black/60 text-white">
                          +{service.images.length - 1} more
                        </Badge>
                      )}
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex gap-2">
                        <Badge className={getCategoryColor(service.category)}>{service.category}</Badge>
                        <Badge variant="outline" className="capitalize">
                          {service.type === 'service' ? <Briefcase className="w-3 h-3 mr-1" /> : <Package className="w-3 h-3 mr-1" />}
                          {service.type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1 text-purple-600 font-semibold">
                        <Clock className="w-4 h-4" />
                        <span>{service.time_cost}h</span>
                      </div>
                    </div>
                    <CardTitle className="mt-2" data-testid={`service-title-${service.id}`}>{service.title}</CardTitle>
                    <CardDescription className="space-y-1">
                      <div className="flex items-center gap-1 text-sm">
                        <User className="w-3 h-3" />
                        <Link 
                          to={`/user/${service.username}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                          data-testid={`username-link-${service.id}`}
                        >
                          {service.username}
                        </Link>
                      </div>
                      {/* Distance display */}
                      {service.distance !== null && service.distance !== undefined ? (
                        <div className="flex items-center gap-1 text-sm text-green-600">
                          <MapPin className="w-3 h-3" />
                          <span>{service.distance} km away</span>
                        </div>
                      ) : service.provider_city && service.provider_country ? (
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <MapPin className="w-3 h-3" />
                          <span>{service.provider_city}, {service.provider_country}</span>
                        </div>
                      ) : (
                        service.provider_location_enabled === false && (
                          <div className="flex items-center gap-1 text-sm text-gray-400">
                            <MapPin className="w-3 h-3" />
                            <span>Location not set</span>
                          </div>
                        )
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm" data-testid={`service-description-${service.id}`}>{service.description}</p>
                    {service.detailed_description && (
                      <p className="text-gray-500 text-xs mt-2 line-clamp-3">{service.detailed_description}</p>
                    )}
                  </CardContent>
                  <CardFooter>
                    <Button
                      className="w-full"
                      onClick={() => handleRequestService(service)}
                      disabled={requesting === service.id}
                      data-testid={`request-service-${service.id}`}
                    >
                      {requesting === service.id ? 'Requesting...' : `Request ${service.type === 'service' ? 'Service' : 'Good'}`}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Marketplace;